import {
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

type Primitive = string | number | boolean;
type QueryValue = Primitive | null | undefined;
type QueryRecord = Record<string, QueryValue>;
type ParamValue = QueryValue | Primitive[];
type ParamRecord = Record<string, ParamValue>;

export interface ApiRequestOptions {
  headers?: QueryRecord;
  params?: ParamRecord;
}

@Injectable({ providedIn: 'root' })
export class ApiClientService {
  constructor(private readonly http: HttpClient) {}

  get<T>(url: string, options: ApiRequestOptions = {}): Observable<T> {
    return this.http.get<T>(url, this.buildOptions(options));
  }

  getBlob(url: string, options: ApiRequestOptions = {}): Observable<Blob> {
    return this.http.get(url, {
      ...this.buildOptions(options),
      responseType: 'blob',
    });
  }

  getById<T>(
    baseUrl: string,
    id: string | number,
    options: ApiRequestOptions = {},
  ): Observable<T> {
    return this.get<T>(this.appendPath(baseUrl, id), options);
  }

  getFileByAttachmentId(
    baseUrl: string,
    attachmentId: string | number,
    options: ApiRequestOptions = {},
  ): Observable<Blob> {
    return this.getBlob(this.appendPath(baseUrl, attachmentId), options);
  }

  post<T>(
    url: string,
    body: unknown = {},
    options: ApiRequestOptions = {},
  ): Observable<T> {
    return this.http.post<T>(url, body, this.buildOptions(options));
  }

  put<T>(
    url: string,
    body: unknown,
    options: ApiRequestOptions = {},
  ): Observable<T> {
    return this.http.put<T>(url, body, this.buildOptions(options));
  }

  patch<T>(
    url: string,
    body: unknown = {},
    options: ApiRequestOptions = {},
  ): Observable<T> {
    return this.http.patch<T>(url, body, this.buildOptions(options));
  }

  delete<T>(
    url: string,
    options: ApiRequestOptions & { body?: unknown } = {},
  ): Observable<T> {
    const { body, ...requestOptions } = options;
    return this.http.delete<T>(url, {
      ...this.buildOptions(requestOptions),
      body,
    });
  }

  uploadFile<T>(
    url: string,
    file: File,
    fieldName = 'file',
    extraFields: QueryRecord = {},
    options: Omit<ApiRequestOptions, 'headers'> = {},
  ): Observable<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    Object.entries(extraFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    return this.http.post<T>(url, formData, this.buildOptions(options));
  }

  /**
   * Posts a pre-built `FormData` body. Content-Type is deliberately left unset
   * so the browser adds `multipart/form-data` together with the part boundary;
   * never pass a Content-Type header here or the boundary is lost.
   */
  postMultipart<T>(
    url: string,
    formData: FormData,
    options: ApiRequestOptions = {},
  ): Observable<T> {
    return this.http.post<T>(url, formData, this.buildOptions(options));
  }

  private buildOptions(options: ApiRequestOptions) {
    const built: {
      headers?: HttpHeaders;
      params?: HttpParams;
    } = {};

    if (options.headers) {
      built.headers = this.buildHeaders(options.headers);
    }

    if (options.params) {
      built.params = this.buildParams(options.params);
    }

    return built;
  }

  private buildHeaders(headers: QueryRecord): HttpHeaders {
    return Object.entries(headers).reduce(
      (httpHeaders, [key, value]) =>
        value === null || value === undefined
          ? httpHeaders
          : httpHeaders.set(key, String(value)),
      new HttpHeaders(),
    );
  }

  private buildParams(params: ParamRecord): HttpParams {
    return Object.entries(params).reduce((httpParams, [key, value]) => {
      if (value === null || value === undefined) {
        return httpParams;
      }

      // Array values are repeated (`?attachmentTypes=IMAGE&attachmentTypes=DOCUMENT`)
      // so list-valued query params keep their per-item order.
      if (Array.isArray(value)) {
        return value.reduce((acc, item) => acc.append(key, String(item)), httpParams);
      }

      return httpParams.set(key, String(value));
    }, new HttpParams());
  }

  private appendPath(baseUrl: string, id: string | number): string {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${normalizedBase}/${id}`;
  }
}
